// © 2025 GridStorm / Tekivex — All Rights Reserved
// Unauthorized reproduction or distribution is prohibited.
/**
 * @gridstorm/dataflow-sentinel — domain types.
 *
 * The Sentinel is the "smart" layer: an anomaly event from the engine triggers
 * an investigation that gathers grounded statistical evidence (column stats,
 * recent window, cross-column correlation) and produces a Finding — a small,
 * severity-ranked, explained alert. An optional, pluggable AI provider enriches
 * the prose; the Finding is fully useful without one.
 */

import type { AnomalyEvent, StreamRow } from '@gridstorm/dataflow-core';

export type FindingSeverity = 'info' | 'warning' | 'critical';

/** A single piece of grounded evidence behind a finding. */
export interface Evidence {
  label: string;
  value: string;
}

export interface Finding {
  id:         string;
  timestamp:  number;
  severity:   FindingSeverity;
  title:      string;
  column:     string;
  value:      number;
  /** Grounded, statistical explanation (template-built; AI may rewrite). */
  cause:      string;
  evidence:   Evidence[];
  /** Most-correlated other column, if any. */
  relatedColumn?:   string;
  suggestedAction:  string;
  /** 0..1 heuristic confidence from |z| and sample size. */
  confidence: number;
  /** True once an AI provider has rewritten cause/action. */
  enriched:   boolean;
}

export interface SentinelConfig {
  enabled:        boolean;
  /** Only investigate anomalies at or above this severity. */
  minSeverity:    FindingSeverity;
  /** Min ms between investigations (cost guard). */
  rateLimitMs:    number;
  /** Suppress repeat findings for the same column within this window (ms). */
  dedupeWindowMs: number;
  /** Ask the configured AI provider to enrich finding prose. */
  useAi:          boolean;
}

export const DEFAULT_SENTINEL_CONFIG: SentinelConfig = {
  enabled:        false,
  minSeverity:    'warning',
  rateLimitMs:    4000,
  dedupeWindowMs: 15000,
  useAi:          true,
};

/** Input bundle the investigator needs. */
export interface InvestigationInput {
  anomaly: AnomalyEvent;
  /** Recent row buffer (most-recent last). */
  rows:    readonly StreamRow[];
}

export type { AnomalyEvent, StreamRow };
